import React, { useState, useEffect, useMemo } from 'react';
import {
    FaPlus, FaTrash, FaPen, FaGem, FaTimes, FaCamera, FaChevronDown,
    FaCheckCircle, FaClock, FaExclamationTriangle, FaHistory
} from 'react-icons/fa';
import {
    addToCollection, updateInCollection, deleteFromCollection,
    uploadWishlistImage, deleteWishlistImage
} from '../../hooks/useFirestore';

// ── VisionCard ──────────────────────────────────────────────────

const VisionCard = ({ item, L }) => {
    const [now, setNow] = useState(new Date());
    const [showPayForm, setShowPayForm] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [payAmount, setPayAmount] = useState('');
    const [payNote, setPayNote] = useState('');
    const [paying, setPaying] = useState(false);

    useEffect(() => {
        if (item.status === 'completed') return;
        const t = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(t);
    }, [item.status]);

    const totalAmount = Number(item.amount) || 0;
    const totalPaid = Number(item.totalPaid) || 0;
    const pending = Math.max(0, totalAmount - totalPaid);
    const paidPct = totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const targetDay = new Date(item.targetDate + 'T00:00:00');
    const daysLeft = Math.max(1, Math.ceil((targetDay - today) / 86400000));
    const amtPerDay = pending > 0 ? Math.ceil(pending / daysLeft) : 0;

    const diffMs = new Date(item.targetDate + 'T23:59:59') - now;
    const isOverdue = diffMs < 0;
    const absDiff = Math.abs(diffMs);
    const absDays = Math.floor(absDiff / 86400000);
    const absHours = Math.floor((absDiff % 86400000) / 3600000);

    const handleAddPayment = async (e) => {
        e.preventDefault();
        if (!payAmount || Number(payAmount) <= 0) return;
        setPaying(true);
        const newTotalPaid = totalPaid + Number(payAmount);
        await updateInCollection('wishlistItems', item.id, {
            payments: [...(item.payments || []), { amount: Number(payAmount), date: new Date().toISOString(), note: payNote }],
            totalPaid: newTotalPaid,
            status: newTotalPaid >= totalAmount ? 'completed' : 'active',
        });
        setPayAmount(''); setPayNote(''); setShowPayForm(false);
        setPaying(false);
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${item.name}"?`)) return;
        if (item.imagePath) await deleteWishlistImage(item.imagePath);
        await deleteFromCollection('wishlistItems', item.id);
    };

    return (
        <div className={`${L.card} overflow-hidden`}>
            {/* Image Banner */}
            {item.imageUrl ? (
                <div className="-mx-6 -mt-6 mb-4 h-40 overflow-hidden">
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                </div>
            ) : (
                <div className="-mx-6 -mt-6 mb-4 h-24 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                    <FaGem className="text-3xl text-blue-300" />
                </div>
            )}

            {/* Completed badge */}
            {item.status === 'completed' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full mb-2">
                    <FaCheckCircle /> Fully Paid
                </span>
            )}

            {/* Name + target */}
            <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                    <h4 className={`text-sm font-bold ${L.text}`}>{item.name}</h4>
                    <p className={`text-[10px] ${L.textMuted} mt-0.5`}>
                        Target: {'\u20B9'}{totalAmount.toLocaleString()} {'\u00B7'} by {new Date(item.targetDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                </div>
                <div className="flex gap-1 shrink-0">
                    <button onClick={handleDelete} className="p-1.5 text-gray-400 hover:text-rose-500 transition-colors"><FaTrash className="text-[9px]" /></button>
                </div>
            </div>

            {/* Countdown */}
            {item.status === 'completed' ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-xl mb-3">
                    <FaCheckCircle className="text-emerald-500 text-sm" />
                    <span className="text-sm font-bold text-emerald-600">Goal Achieved!</span>
                </div>
            ) : isOverdue ? (
                <div className="px-3 py-2 bg-rose-50 rounded-xl mb-3">
                    <div className="flex items-center gap-2">
                        <FaExclamationTriangle className="text-rose-400 text-xs" />
                        <p className="text-[10px] text-rose-400 uppercase tracking-widest font-semibold">Overdue by</p>
                    </div>
                    <p className="text-xl font-bold text-rose-600 mt-0.5">{absDays}d {absHours}h</p>
                </div>
            ) : (
                <div className="px-3 py-2 bg-blue-50 rounded-xl mb-3">
                    <div className="flex items-center gap-2">
                        <FaClock className="text-blue-400 text-xs" />
                        <p className="text-[10px] text-blue-400 uppercase tracking-widest font-semibold">Time remaining</p>
                    </div>
                    <p className="text-xl font-bold text-blue-700 mt-0.5">{absDays}d {absHours}h</p>
                </div>
            )}

            {/* Progress Bar */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${L.text}`}>
                        {'\u20B9'}{totalPaid.toLocaleString()} paid ({paidPct.toFixed(0)}%)
                    </span>
                    <span className={`text-xs ${L.textMuted}`}>
                        {'\u20B9'}{pending.toLocaleString()} left
                    </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-700 ${paidPct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.min(100, paidPct)}%` }}
                    />
                </div>
            </div>

            {/* Analytics */}
            {item.status !== 'completed' && (
                <p className="text-[10px] text-gray-400 mt-1">
                    {'\u20B9'}{amtPerDay.toLocaleString()}/day needed {'\u00B7'} {daysLeft} days left {'\u00B7'} {(100 - paidPct).toFixed(0)}% pending
                </p>
            )}

            {/* Add Payment */}
            {item.status !== 'completed' && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                    <button onClick={() => setShowPayForm(p => !p)}
                        className="w-full bg-gray-100 text-gray-700 font-semibold rounded-xl text-xs px-4 py-2 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
                        <FaPlus className="text-[9px]" /> Add Payment
                    </button>
                    {showPayForm && (
                        <form onSubmit={handleAddPayment} className="mt-2 space-y-2">
                            <div className="flex gap-2">
                                <input type="number" placeholder="Amount" value={payAmount}
                                    onChange={e => setPayAmount(e.target.value)} required
                                    className={`flex-1 ${L.input} text-xs !py-2`} />
                                <input type="text" placeholder="Note (optional)" value={payNote}
                                    onChange={e => setPayNote(e.target.value)}
                                    className={`flex-1 ${L.input} text-xs !py-2`} />
                            </div>
                            <button type="submit" disabled={paying}
                                className={`w-full ${L.btnPrimary} text-xs py-2`}>
                                {paying ? 'Saving...' : `Pay \u20B9${Number(payAmount || 0).toLocaleString()}`}
                            </button>
                        </form>
                    )}
                </div>
            )}

            {/* Payment History */}
            {(item.payments || []).length > 0 && (
                <div className="mt-2 border-t border-gray-100 pt-2">
                    <button onClick={() => setShowHistory(p => !p)}
                        className="flex items-center gap-1.5 text-[10px] text-blue-500 hover:text-blue-600 transition-colors">
                        <FaHistory className="text-[8px]" />
                        {showHistory ? 'Hide' : 'Show'} {item.payments.length} payment{item.payments.length !== 1 ? 's' : ''}
                    </button>
                    {showHistory && (
                        <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {[...(item.payments || [])].reverse().map((p, i) => (
                                <div key={i} className="flex items-center justify-between text-[10px] text-gray-500 px-2 py-1 bg-gray-50 rounded-lg">
                                    <span>{p.note || 'Payment'} {'\u00B7'} {new Date(p.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                                    <span className="font-semibold text-emerald-600">+{'\u20B9'}{Number(p.amount).toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Add Item Modal ──────────────────────────────────────────────

const AddItemModal = ({ L, onClose }) => {
    const [form, setForm] = useState({ name: '', amount: '', targetDate: '' });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.amount || !form.targetDate) return;
        setSaving(true);
        try {
            const docRef = await addToCollection('wishlistItems', {
                name: form.name,
                amount: Number(form.amount),
                targetDate: form.targetDate,
                imageUrl: '',
                imagePath: '',
                payments: [],
                totalPaid: 0,
                status: 'active',
            });
            if (imageFile && docRef?.id) {
                const { imageUrl, imagePath } = await uploadWishlistImage(docRef.id, imageFile);
                await updateInCollection('wishlistItems', docRef.id, { imageUrl, imagePath });
            }
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const handleImagePick = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5 MB'); return; }
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = ev => setImagePreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    return (
        <div className={L.modal} onClick={onClose}>
            <div className={L.modalCard} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <h3 className={`text-lg font-bold ${L.text}`}>Add Vision Item</h3>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Image Picker */}
                    <div>
                        <input type="file" accept="image/*" onChange={handleImagePick} className="hidden" id="wishlist-img-picker" />
                        <label htmlFor="wishlist-img-picker" className="cursor-pointer block">
                            {imagePreview ? (
                                <img src={imagePreview} alt="preview" className="w-full h-36 object-cover rounded-xl border border-gray-200" />
                            ) : (
                                <div className="w-full h-36 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:bg-gray-100 hover:border-gray-300 transition-colors">
                                    <FaCamera className="text-2xl mb-1" />
                                    <span className="text-xs">Click to add image</span>
                                </div>
                            )}
                        </label>
                    </div>
                    <input type="text" placeholder="Item name (e.g., MacBook Pro)" value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })} required className={L.input} />
                    <div className="grid grid-cols-2 gap-3">
                        <input type="number" placeholder="Target amount" value={form.amount}
                            onChange={e => setForm({ ...form, amount: e.target.value })} required className={L.input} />
                        <input type="date" value={form.targetDate}
                            onChange={e => setForm({ ...form, targetDate: e.target.value })} required className={L.input} />
                    </div>
                    <button type="submit" disabled={saving} className={`w-full py-3 ${L.btnPrimary}`}>
                        {saving ? 'Creating...' : 'Add to Vision Board'}
                    </button>
                </form>
            </div>
        </div>
    );
};

// ── Edit Item Modal ─────────────────────────────────────────────

const EditItemModal = ({ L, item, onClose }) => {
    const [form, setForm] = useState({
        name: item.name || '',
        amount: item.amount || '',
        targetDate: item.targetDate || '',
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(item.imageUrl || null);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const updates = {
                name: form.name,
                amount: Number(form.amount),
                targetDate: form.targetDate,
            };
            if (imageFile) {
                if (item.imagePath) await deleteWishlistImage(item.imagePath);
                const { imageUrl, imagePath } = await uploadWishlistImage(item.id, imageFile);
                updates.imageUrl = imageUrl;
                updates.imagePath = imagePath;
            }
            await updateInCollection('wishlistItems', item.id, updates);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const handleImagePick = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5 MB'); return; }
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = ev => setImagePreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    return (
        <div className={L.modal} onClick={onClose}>
            <div className={L.modalCard} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <h3 className={`text-lg font-bold ${L.text}`}>Edit Vision Item</h3>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <input type="file" accept="image/*" onChange={handleImagePick} className="hidden" id="wishlist-edit-img" />
                        <label htmlFor="wishlist-edit-img" className="cursor-pointer block">
                            {imagePreview ? (
                                <img src={imagePreview} alt="preview" className="w-full h-36 object-cover rounded-xl border border-gray-200" />
                            ) : (
                                <div className="w-full h-36 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:bg-gray-100 hover:border-gray-300 transition-colors">
                                    <FaCamera className="text-2xl mb-1" />
                                    <span className="text-xs">Click to add image</span>
                                </div>
                            )}
                        </label>
                    </div>
                    <input type="text" placeholder="Item name" value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })} required className={L.input} />
                    <div className="grid grid-cols-2 gap-3">
                        <input type="number" placeholder="Target amount" value={form.amount}
                            onChange={e => setForm({ ...form, amount: e.target.value })} required className={L.input} />
                        <input type="date" value={form.targetDate}
                            onChange={e => setForm({ ...form, targetDate: e.target.value })} required className={L.input} />
                    </div>
                    <button type="submit" disabled={saving} className={`w-full py-3 ${L.btnPrimary}`}>
                        {saving ? 'Saving...' : 'Update'}
                    </button>
                </form>
            </div>
        </div>
    );
};

// ── Main VisionTab ──────────────────────────────────────────────

const VisionTab = ({ L, wishlistItems }) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [showCompleted, setShowCompleted] = useState(false);

    const activeItems = useMemo(() => (wishlistItems || []).filter(i => i.status !== 'completed'), [wishlistItems]);
    const completedItems = useMemo(() => (wishlistItems || []).filter(i => i.status === 'completed'), [wishlistItems]);

    const totalTarget = useMemo(() => (wishlistItems || []).reduce((s, i) => s + (Number(i.amount) || 0), 0), [wishlistItems]);
    const totalPaidAll = useMemo(() => (wishlistItems || []).reduce((s, i) => s + (Number(i.totalPaid) || 0), 0), [wishlistItems]);
    const totalPending = totalTarget - totalPaidAll;
    const overallPct = totalTarget > 0 ? (totalPaidAll / totalTarget) * 100 : 0;

    return (
        <div className="space-y-6">
            {/* Header + Add Button */}
            <div className="flex items-center justify-between">
                <div>
                    <p className={L.textLabel}>Your Aspirations</p>
                    <h3 className={`text-lg font-bold ${L.text}`}>Vision Board</h3>
                </div>
                <button onClick={() => setShowAddModal(true)} className={`${L.btnPrimary} flex items-center gap-2`}>
                    <FaPlus className="text-[10px]" /> Add Item
                </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={L.card}>
                    <p className={L.textLabel}>Active Items</p>
                    <p className={`text-2xl font-bold ${L.text} mt-1`}>{activeItems.length}</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Total Target</p>
                    <p className={`text-2xl font-bold ${L.accent} mt-1`}>{'\u20B9'}{totalTarget.toLocaleString()}</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Total Saved</p>
                    <p className={`text-2xl font-bold ${L.positive} mt-1`}>{'\u20B9'}{totalPaidAll.toLocaleString()}</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Still Needed</p>
                    <p className={`text-2xl font-bold ${L.negative} mt-1`}>{'\u20B9'}{totalPending.toLocaleString()}</p>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
                        <div className="h-full bg-blue-500 rounded-full transition-all duration-700" style={{ width: `${Math.min(100, overallPct)}%` }} />
                    </div>
                </div>
            </div>

            {/* Active Items Grid */}
            {activeItems.length === 0 ? (
                <div className={`${L.card} text-center py-16`}>
                    <FaGem className="text-4xl text-gray-300 mx-auto mb-3" />
                    <p className={`text-sm ${L.textMuted} mb-4`}>No active wishes yet. What are you saving for?</p>
                    <button onClick={() => setShowAddModal(true)} className={L.btnPrimary}>Add First Item</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {activeItems.map(item => (
                        <VisionCard key={item.id} item={item} L={L} />
                    ))}
                </div>
            )}

            {/* Completed Section */}
            {completedItems.length > 0 && (
                <div className={L.card}>
                    <button onClick={() => setShowCompleted(p => !p)}
                        className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                            <FaCheckCircle className="text-emerald-500" />
                            <h3 className={`text-sm font-bold ${L.text}`}>Completed ({completedItems.length})</h3>
                        </div>
                        <FaChevronDown className={`text-gray-400 text-xs transition-transform duration-200 ${showCompleted ? 'rotate-180' : ''}`} />
                    </button>
                    {showCompleted && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {completedItems.map(item => (
                                <VisionCard key={item.id} item={item} L={L} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Add Modal */}
            {showAddModal && <AddItemModal L={L} onClose={() => setShowAddModal(false)} />}

            {/* Edit Modal */}
            {editItem && <EditItemModal L={L} item={editItem} onClose={() => setEditItem(null)} />}
        </div>
    );
};

export default VisionTab;
